import { useState } from "react";
import { ApiError, requestCode, verifyCode } from "../lib/api";

const ERRORS: Record<string, string> = {
  "invalid-email": "Enter a valid email address.",
  "invalid-code": "Wrong code, try again.",
  "too-many-attempts": "Too many attempts. Ask for a new code.",
  "rate-limited": "Too many requests. Try again in a few minutes.",
  "challenge-failed": "We couldn't verify you're human. Reload and try again.",
  "mail-failed": "We couldn't send the email. Try again in a moment.",
};

const message = (err: unknown) =>
  (err instanceof ApiError && ERRORS[err.code]) || "Something went wrong. Try again.";

export function SignIn({ onDone, onBack }: { onDone: (email: string) => void; onBack: () => void }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Re-mounts the error node so the shake replays on a repeat failure.
  const [errorKey, setErrorKey] = useState(0);

  const showError = (text: string) => {
    setError(text);
    setErrorKey((k) => k + 1);
  };

  const send = async () => {
    if (busy) return;
    const value = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      showError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { challengeId: id } = await requestCode(value);
      setEmail(value);
      setChallengeId(id);
      setCode("");
    } catch (err) {
      showError(message(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (busy || code.length !== 6) return;
    setBusy(true);
    setError("");
    try {
      const { email: signedIn } = await verifyCode(challengeId, code);
      onDone(signedIn);
    } catch (err) {
      showError(message(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <div className="auth-card">
        <div className="stack-tight">
          <h1 className="auth-title">Sign in</h1>
          <p className="subtitle">
            Keep your learning history under your email. We&rsquo;ll send you a one-time code — no
            password.
          </p>
        </div>

        {!challengeId ? (
          <div className="answer-row">
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") void send();
              }}
              placeholder="you@example.com"
              aria-label="Email address"
              autoComplete="email"
              autoFocus
            />
            <button type="button" className="btn btn-primary" onClick={() => void send()} disabled={busy}>
              Send code
            </button>
          </div>
        ) : (
          <div className="auth-step">
            <div className="auth-sent">
              Code sent to <b>{email}</b>
            </div>
            <div className="answer-row">
              <input
                className="input otp-input"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void verify();
                }}
                inputMode="numeric"
                placeholder="6-digit code"
                aria-label="Six-digit code"
                autoComplete="one-time-code"
                autoFocus
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void verify()}
                disabled={busy}
              >
                Verify
              </button>
            </div>
            <div>
              <button
                type="button"
                className="btn-link"
                onClick={() => {
                  setChallengeId("");
                  setCode("");
                  setError("");
                }}
              >
                Use another email
              </button>
            </div>
          </div>
        )}

        {error && (
          <div key={errorKey} className="error error-shake" role="alert">
            {error}
          </div>
        )}

        <div>
          <button type="button" className="btn-link btn-back" onClick={onBack}>
            ← Back to practice
          </button>
        </div>
      </div>
    </div>
  );
}
